import { useState } from 'react';
import { buildDemoRound, getRandomDemoMovie } from './demoQuestions';
import styles from './DemoQuiz.module.css';

// The sample is deliberately shorter than a real challenge (8 questions) and
// never awards anything; the copy below says so wherever a score appears.
export const DEMO_QUESTION_COUNT = 5;
export const REAL_QUESTION_COUNT = 8;

function DemoMovieSelector({ movie, onShuffle }) {
    return (
        <div className={styles.movieSelector}>
            <span>Sample title: <strong>{movie.title}</strong></span>
            <button type="button" onClick={onShuffle}>Try another title</button>
        </div>
    );
}

function DemoProgress({ current, total, complete = false }) {
    const pct = complete ? 100 : (current / total) * 100;
    return (
        <div className={styles.progressWrap}>
            <div className={styles.progressMeta}>
                <span>{complete ? 'Sample complete' : `Sample question ${current + 1} of ${total}`}</span>
                <strong>{complete ? '' : `Real challenges: ${REAL_QUESTION_COUNT}`}</strong>
            </div>
            <div className={styles.progressTrack}><span style={{ width: `${pct}%` }} /></div>
        </div>
    );
}

function DemoQuestion({ question, selected, onSelect }) {
    return (
        <div className={styles.questionPane}>
            <p className={styles.difficulty}>{question.difficulty}</p>
            <h3>{question.question}</h3>
            <div className={styles.answerGrid}>
                {question.choices.map((choice) => {
                    const isSelected = selected === choice;
                    const isCorrect = choice === question.answer;
                    const resultClass = selected && isSelected ? (isCorrect ? styles.correct : styles.incorrect) : '';
                    return (
                        <button
                            type="button"
                            className={`${styles.answerButton} ${resultClass}`}
                            disabled={Boolean(selected)}
                            onClick={() => onSelect(choice)}
                            key={choice}
                        >
                            <span>{isSelected ? (isCorrect ? '✓' : '✕') : '○'}</span>
                            {choice}
                        </button>
                    );
                })}
            </div>
            {selected && (
                <div className={selected === question.answer ? styles.feedbackCorrect : styles.feedbackIncorrect} role="status">
                    <strong>{selected === question.answer ? '✓ Correct' : '✗ Incorrect'}</strong>
                    <span>{question.explanation}</span>
                </div>
            )}
        </div>
    );
}

function DemoResults({ score, total, onRetry }) {
    return (
        <div className={styles.resultsPane}>
            <DemoProgress current={total} total={total} complete />
            <p className={styles.scoreLine}>{score} of {total} correct</p>
            <h3>{score === total ? 'Clean sweep.' : score >= total - 1 ? 'Nearly perfect.' : 'Not bad for a warm-up.'}</h3>
            <p>
                This was a practice round, so no coin was awarded. A real challenge has {REAL_QUESTION_COUNT} questions:
                5 correct earns Bronze, 6–7 earns Silver, and a perfect 8 earns Gold.
            </p>
            <div className={styles.resultCtas}>
                <a href="/register">Start Earning Coins</a>
                <button type="button" onClick={onRetry}>Try another title</button>
            </div>
        </div>
    );
}

function DemoQuiz() {
    const [movie, setMovie] = useState(() => getRandomDemoMovie());
    const [questions, setQuestions] = useState(() => buildDemoRound(movie, DEMO_QUESTION_COUNT, DEMO_QUESTION_COUNT));
    const [index, setIndex] = useState(0);
    const [selected, setSelected] = useState('');
    const [score, setScore] = useState(0);
    const [complete, setComplete] = useState(false);

    const restartWithMovie = () => {
        setIndex(0);
        setSelected('');
        setScore(0);
        setComplete(false);
        const nextMovie = getRandomDemoMovie();
        setMovie(nextMovie);
        setQuestions(buildDemoRound(nextMovie, DEMO_QUESTION_COUNT, DEMO_QUESTION_COUNT));
    };

    const handleSelect = (choice) => {
        if (selected) return;
        setSelected(choice);
        if (choice === questions[index].answer) {
            setScore((value) => value + 1);
        }
        window.setTimeout(() => {
            setSelected('');
            if (index + 1 >= questions.length) {
                setComplete(true);
            } else {
                setIndex((value) => value + 1);
            }
        }, 2000);
    };

    return (
        <section className={styles.demoCard} style={{ '--demo-accent': movie.accent }} aria-label="Sample quiz: a shortened practice round that does not award a coin">
            <DemoMovieSelector movie={movie} onShuffle={restartWithMovie} />
            <div className={styles.demoShell}>
                <div className={styles.posterCard}>
                    <span>{movie.posterEmoji}</span>
                    <strong>{movie.title}</strong>
                    <em>{movie.year}</em>
                </div>
                <div className={styles.quizPanel}>
                    {complete ? (
                        <DemoResults score={score} total={questions.length} onRetry={restartWithMovie} />
                    ) : (
                        <>
                            <DemoProgress current={index} total={questions.length} />
                            <DemoQuestion question={questions[index]} selected={selected} onSelect={handleSelect} />
                        </>
                    )}
                </div>
            </div>
        </section>
    );
}

export default DemoQuiz;
